import './manageDicts.scss'
import EditDict from "./editDict";
import AddDict from "./addDict";
import ButtonField from "../../../../components/buttonField/buttonField";
import Spinner from "../../../../components/spinner/spinner";
import ConfirmModal from "../../../../components/modalWindow/confirmModal";
import { apiContext } from "../../../../contexts/apiContext";
import { ButtonFieldProps } from "../../../../components/buttonField/types/buttonFieldProps";
import { stylizationVerticalAlign } from "../../../../components/shared/stylization";
import { ConfirmModalProps } from "../../../../components/modalWindow/types/confirmModalProps";
import { ReactComponent as addCircleIcon } from '../../../../icons/add_circle.svg';
import { ReactComponent as Edit } from '../../../../icons/edit.svg';
import { ReactComponent as Delete } from '../../../../icons/delete.svg';
import { useContext, useState } from "react";
import { classNameBtn, iconPosition } from "../../../../components/shared/button/types/buttonProps";
import { checkOverwrite, getActionBtnSnackbar, getNextRecOrder } from "../../../../helpers/mutableDictionaries";
import { addDictRecord, deleteDictRecord, editDictRecord } from "../../../../services/dictionariesService/dictionariesService";
import { AddDictRecordProps, DeleteDictRecordProps, EditDictRecordProps } from "../../../../services/dictionariesService/types/dictionariesServiceProps";
import { AddEditDictProps, EditActiveBtnActions, EditDictsViews, ManageDictEditionProps, MutableDict } from "./types/manageDictsProps";
import ToggleButton from '../../../../components/toggleButton/toggleButton';

const ManageDictEdition = (props: ManageDictEditionProps) => {
    const api = useContext(apiContext);
    const [currentView, setCurrentView] = useState<EditDictsViews>(EditDictsViews.EditDict);
    const [btnToggle, setBtnToggle] = useState<boolean>(true); 
    const [editedDictRecord, setEditedDictRecord] = useState<MutableDict>({});
    const [editedDict, setEditedDict] = useState<MutableDict[]>([]);
    const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(false);
    const [disableDeleteBtn, setDisableDeleteBtn] = useState<boolean>(false);
    const [confirmation, setConfirmation] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const handleEditClick = () => {
        setCurrentView(EditDictsViews.EditDict);
        setBtnToggle(!btnToggle);
        setEditedDictRecord({});
    }

    const handleAddClick = () => {
        setCurrentView(EditDictsViews.AddRecord);
        setBtnToggle(!btnToggle);
        setEditedDictRecord({});
    }

    // Action buttons
    const getActionBtnProps = (title: string, onClick: React.MouseEventHandler<HTMLButtonElement>, btnClass: string, disabled: boolean,
        icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>): ButtonFieldProps => {

        return {
            BtnProps: {
                onClick: onClick,
                style: {
                    width: '240px',
                    height: '45px',
                    gap: '10px',
                    padding: '8px 14px'
                },
                className: `${btnClass} body-txt-medium`,
                disabled: disabled
            },
            placeHolder: title,
            placeHolderStyle: stylizationVerticalAlign,
            additionalIcon: icon,
            iconPosition: iconPosition.Start
        };
    }

    const handleReturnClick = () => {
        props.onClose();
        setEditedDict([]);
    }

    const handleSaveClick = () => {
        switch (currentView) {
            case EditDictsViews.AddRecord:
                addNewDictRec();
                break;
            case EditDictsViews.EditDict:
                editExistingDictRec();
        }
    }

    const handleDeleteClick = () => {
        setConfirmation(true);
    }

    const addNewDictRec = () => {
        setLoading(true);

        var addDictRecordProps: AddDictRecordProps = {
            dictLayerId: props.dictDefinition[1],
            newRecord: editedDictRecord,
            apiBase: api.apiUrl + api.apiVersion
        }

        addDictRecordProps.newRecord.KOLEJNOSC = getNextRecOrder(editedDict);

        addDictRecord(addDictRecordProps)
        .then(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Add, true);
            props.onClose();
        })
        .catch(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Add, false);
            setLoading(false);
        })
    }

    const editExistingDictRec = () => {
        setLoading(true);

        var editDictRecordProps: EditDictRecordProps = {
            dictLayerId: props.dictDefinition[1],
            newRecord: editedDictRecord,
            overwrite: checkOverwrite(editedDictRecord, editedDict),
            apiBase: api.apiUrl + api.apiVersion
        }

        if (!editDictRecordProps.overwrite) editDictRecordProps.newRecord.POPRZEDNIK = editDictRecordProps.newRecord.ID;

        editDictRecord(editDictRecordProps)
        .then(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Edit, true);
            props.onClose();
        })
        .catch(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Edit, false);
            setLoading(false);
        })
    }

    const deleteDictRec = () => {
        setLoading(true);

        var deleteDictRecordProps: DeleteDictRecordProps = {
            dictLayerId: props.dictDefinition[1],
            recordId: editedDictRecord.ID!,
            apiBase: api.apiUrl + api.apiVersion
        }
 
        deleteDictRecord(deleteDictRecordProps)
        .then(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Delete, true);
            props.onClose();
        })
        .catch(() => {
            getActionBtnSnackbar(EditActiveBtnActions.Delete, false);
            setLoading(false);
        })
    }

    // Confirm delete
    const cancel = () => {
        setConfirmation(false);
    }

    const confirm = () => {
        deleteDictRec();
        setConfirmation(false);
    }

    var confirmationProps: ConfirmModalProps = {
        width: "660px",
        height: "auto",
        confirmTitle: "Usuń",
        onConfirm: confirm,
        cancelTitle: "Anuluj",
        onCancel: cancel,
        question: "Czy na pewno chcesz usunąć wartość słownikową?"
    }

    var addEditDictProps: AddEditDictProps = {
        dictDefinition: props.dictDefinition,
        setDisableSaveBtn: setDisableSaveBtn,
        setDisableDeleteBtn: setDisableDeleteBtn,
        setEditedDict: setEditedDict,
        editedDict: editedDict,
        setEditedDictRecord: setEditedDictRecord,
        editedDictRecord: editedDictRecord,
        setLoading: setLoading
    }
    
    return ( 
        <div className="manageDictEditionWrapper">
            <div className="manageDictEditionTitleRow">
                <h3><span>Słownik</span> {props.dictDefinition[0]}</h3>
                <ToggleButton
                    btnToggle={btnToggle}
                    setBtnToggle={setBtnToggle}
                    leftText={"Edytuj słownik"}
                    rightText={"Dodaj nową pozycję"}
                    leftIcon={Edit}
                    rightIcon={addCircleIcon}
                    onChangeLeft={handleEditClick}
                    onChangeRight={handleAddClick}
                />
            </div>
            {currentView === EditDictsViews.EditDict && <EditDict {...addEditDictProps}/>}
            {currentView === EditDictsViews.AddRecord && <AddDict {...addEditDictProps}/>}
            <div className="editDictActionBtns">
                <ButtonField {...getActionBtnProps("Wróć do listy słowników", handleReturnClick, classNameBtn.SmallSecondary, false)}/>
                {currentView === EditDictsViews.EditDict && <ButtonField {...getActionBtnProps("Usuń pozycję słownikową", handleDeleteClick, classNameBtn.SmallWarning, disableDeleteBtn, Delete)}/>}
                <ButtonField {...getActionBtnProps("Zapisz zmiany", handleSaveClick, classNameBtn.Small, disableSaveBtn)}/>
            </div>
            {confirmation && <ConfirmModal {...confirmationProps}/>}
            {loading && <div className="tableSpinner"><Spinner /></div>}  
        </div>
    );
}

export default ManageDictEdition;